import type { ReviewConfidence, PropertyPrior, ChaseRecommendation } from '@/api/client'
import { ChaseDecision } from '@/components/review-chase/chase-decision'
import { ConfidenceIssues } from '@/components/review-chase/confidence-issues'
import { CONFIDENCE_TIER_LABEL, type ConfidenceTier } from '@/hooks/use-review-confidence'

export const PANEL_TIER_CLASSES: Record<ConfidenceTier, string> = {
  high: 'bg-mint/10 border-mint/30',
  mid: 'bg-amber/10 border-amber/30',
  low: 'bg-danger/10 border-danger/30',
}

export const SUMMARY_TIER_CLASSES: Record<ConfidenceTier, string> = {
  high: 'text-mint',
  mid: 'text-amber',
  low: 'text-danger',
}

// The six signal types review-confidence-prompt.ts (admin-api) ships with -
// kept as a plain lookup rather than importing from the backend, same as
// this app's other backend-shaped-but-unpublished constants. Falls back to a
// humanized version of the raw type for one this list doesn't recognize
// (the backend logs a warning but doesn't reject an unrecognized type, so a
// future prompt edit reaching this UI before this map is updated shouldn't
// render nothing).
const SIGNAL_TYPE_LABEL: Record<string, string> = {
  unresolved_issue: 'Unresolved',
  explicit_complaint: 'Complaint',
  unanswered_or_slow: 'Slow reply',
  coordination_friction: 'Coordination',
  went_quiet_after_friction: 'Went quiet',
  no_response_to_review_request: 'No response',
  flat_or_absent_checkout: 'Flat checkout',
}

export function formatSignalType(type: string): string {
  return (
    SIGNAL_TYPE_LABEL[type] ??
    type
      .split('_')
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  )
}

export function ConfidenceSignals({ signals }: { signals: ReviewConfidence['signals'] }) {
  if (signals.length === 0) return null
  return (
    <ul className="space-y-1.5">
      {signals.map((signal, i) => (
        <li key={i} className="flex gap-2 text-xs leading-relaxed text-textMuted">
          <span className="mt-0.5 h-fit flex-none whitespace-nowrap rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-text">
            {formatSignalType(signal.type)}
          </span>
          <span>{signal.detail}</span>
        </li>
      ))}
    </ul>
  )
}

interface ConfidenceDetailProps {
  confidence: ReviewConfidence
  tier: ConfidenceTier
  recommendation?: ChaseRecommendation | null
  propertyPrior?: PropertyPrior | null
  /**
   * With no signals, say so in words. Off for the reservation-detail card,
   * which already prints the tier label above this in larger type - two
   * copies of "Nothing to see" one under the other reads as a bug.
   */
  summaryFallback?: boolean
}

/**
 * Everything behind a five-star confidence score: why it scored that way,
 * what the thread raised, and what to do about it. One component so the
 * Review Chase row panel and the reservation-detail card show the operator
 * the same reasoning - hand-copied renderings had already started to drift
 * before this was extracted.
 */
export function ConfidenceDetail({
  confidence,
  tier,
  recommendation,
  propertyPrior,
  summaryFallback = true,
}: ConfidenceDetailProps) {
  return (
    <>
      {confidence.signals.length > 0 ? (
        <ConfidenceSignals signals={confidence.signals} />
      ) : summaryFallback ? (
        <p className={`text-sm ${SUMMARY_TIER_CLASSES[tier]}`}>
          {confidence.summary || CONFIDENCE_TIER_LABEL[tier]}
        </p>
      ) : null}
      <ConfidenceIssues issues={confidence.facts?.issues ?? []} />
      {recommendation && (
        <ChaseDecision recommendation={recommendation} propertyPrior={propertyPrior} />
      )}
    </>
  )
}
